/**
 * A stat comparison: each row's two values with the label centered, the
 * leader's value bold, each side's league rank in parentheses when the server
 * sends one (ISSUE-988), and a two color bar whose width is the server's
 * integer awaySharePercent. No arithmetic here (game-detail rule 7). Shared by
 * ISSUE-988's season stats and ISSUE-989's game team stats. ISSUE-988 WI-24.
 *
 * ISSUE-1084: a team title row sits under the heading of every section, so
 * each section reads alone. The away team's mark and name sit on the left
 * over the away values, the home team's name and mark on the right over the
 * home values, and each title carries a swatch in its side's bar color, so
 * the color is never the only cue. The bar sides use the `comparisonAway`
 * and `comparisonHome` theme tokens (a blue and an orange, each 3:1 on every
 * surface in both modes), never team colors. Names truncate on one line so
 * both titles stay visible at 320px.
 */
import { cn } from "@sportsmet/ui";
import type { ReactNode } from "react";

import type {
  typeMatchupTeamMark,
  typeStatComparisonRow,
} from "./matchupTypes";
import { FittedTeamName } from "./FittedTeamName";
import { TeamMark } from "./TeamMark";

/** text-caption, the size the legend's team names render at. */
const CAPTION_FONT_PX = 13.5;
import { GameSectionCard } from "./GameSectionCard";

type typeSide = "away" | "home";

const SIDE_COLOR_CLASS: Record<typeSide, string> = {
  away: "bg-comparison-away",
  home: "bg-comparison-home",
};

const SideSwatch = ({ side }: { side: typeSide }): ReactNode => (
  <span
    data-testid={`stat-${side}-swatch`}
    className={cn("h-2 w-4 shrink-0 rounded-full", SIDE_COLOR_CLASS[side])}
    aria-hidden="true"
  />
);

const TeamTitle = ({
  team,
  side,
}: {
  team: typeMatchupTeamMark;
  side: typeSide;
}): ReactNode => {
  const mark = (
    <TeamMark
      name={team.name}
      abbreviation={team.plateText}
      logoUrl={team.logoUrl}
      size="sm"
    />
  );
  const name = (
    <FittedTeamName
      name={team.name}
      shortName={team.shortName ?? null}
      abbreviation={team.abbreviation ?? team.plateText}
      fontPx={CAPTION_FONT_PX}
      className={cn(
        "text-caption text-ink-secondary",
        side === "home" && "text-right",
      )}
      testId={`stat-${side}-team-name`}
    />
  );
  return (
    <div
      data-testid={`stat-${side}-team`}
      className={cn(
        "flex min-w-0 flex-1 items-center gap-2",
        side === "home" ? "justify-end" : "justify-start",
      )}
    >
      {side === "away" ? (
        <>
          {mark}
          {name}
          <SideSwatch side="away" />
        </>
      ) : (
        <>
          <SideSwatch side="home" />
          {name}
          {mark}
        </>
      )}
    </div>
  );
};

// The leader is bold in ink, the trailer regular and muted (spec 7.6). An
// even row, or one the server ranks for neither side, mutes neither.
const valueClass = (
  leader: typeStatComparisonRow["leader"],
  side: typeSide,
): string =>
  leader === side
    ? "text-body-strong text-ink tabular-nums"
    : leader === "away" || leader === "home"
      ? "text-body text-ink-secondary tabular-nums"
      : "text-body text-ink tabular-nums";

const hasText = (value: string | null | undefined): value is string =>
  typeof value === "string" && value !== "";

export const StatComparisonSection = ({
  title,
  rows,
  away,
  home,
}: {
  title: string;
  rows: readonly typeStatComparisonRow[];
  away: typeMatchupTeamMark;
  home: typeMatchupTeamMark;
}): ReactNode => (
  <GameSectionCard testId="stat-comparison" title={title}>
    <div
      data-testid="stat-comparison-teams"
      className="flex items-center justify-between gap-3"
    >
      <TeamTitle team={away} side="away" />
      <TeamTitle team={home} side="home" />
    </div>
    {rows.map((row) => (
      <div
        key={row.key}
        data-testid="stat-comparison-row"
        className="flex flex-col gap-1"
      >
        <div className="flex items-center justify-between gap-2">
          <span
            data-testid="stat-away-value"
            className={valueClass(row.leader, "away")}
          >
            {row.awayValue}
            {hasText(row.awayRankLabel) ? (
              <span
                data-testid="stat-away-rank"
                className="text-caption text-ink-tertiary"
              >
                {" "}
                ({row.awayRankLabel})
              </span>
            ) : null}
          </span>
          <span className="text-small text-ink-secondary text-center">
            {row.label}
          </span>
          <span
            data-testid="stat-home-value"
            className={valueClass(row.leader, "home")}
          >
            {hasText(row.homeRankLabel) ? (
              <span
                data-testid="stat-home-rank"
                className="text-caption text-ink-tertiary"
              >
                ({row.homeRankLabel}){" "}
              </span>
            ) : null}
            {row.homeValue}
          </span>
        </div>
        {row.awaySharePercent === null ||
        row.awaySharePercent === undefined ? null : (
          <div
            data-testid="stat-comparison-bar"
            className="flex h-1 gap-1"
            aria-hidden="true"
          >
            <div
              className={cn("rounded-full", SIDE_COLOR_CLASS.away)}
              style={{ width: `${row.awaySharePercent}%` }}
            />
            <div className={cn("flex-1 rounded-full", SIDE_COLOR_CLASS.home)} />
          </div>
        )}
      </div>
    ))}
  </GameSectionCard>
);
